import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import TrelloBoard, { components as trelloComponents } from 'react-trello';
import moment from 'moment';
import PetCard from './PetCard';
import localStorage from '../../../libs/storageHelper';
import constant from '../../../constants/constants';
import { dragCard, deletePhaseMessage } from '../../../redux/actions/pTBActions';
import MessageModal from '../../../shared/components/Modals/MessageModal';
import PhaseMoveConfirmPopover from './PhaseMoveConfirmPopover';
import PtbCardAnchoredAck from './PtbCardAnchoredAck';

import ResponseMessages from '../../../constants/responseMessages';
import {
  boardingOnlyLanes,
  defaultLanes,
  getVisibleLanes,
  PTB_NEWLY_ADDED_FEEDBACK_MS,
} from './constants';
import { useGetFacilityServices } from '../../App/customize/hooks/useGetFacilityServices';
import RefreshBoardButton from './RefreshBoardButton';
import {
  fetchToken,
  onMessageListener,
  unsubscribeFromFirebaseTopic,
} from '../../App/firebase';
import { PHASES } from '../../../config/app.config';

// react-trello spreads lane.title onto <section class="react-trello-lane">;
// omit it so the full-height lane does not show a native phase-name tooltip.
// LaneHeader still receives title via renderHeader.
function PtbLaneSection({ title, ...rest }) {
  return <trelloComponents.Section {...rest} />;
}

const EMPTY_PHASE_PICKER = {
  isOpen: false,
  direction: null,
  anchorRect: null,
  cardId: null,
  sourceId: null,
  cardDetails: null,
};

const EMPTY_PHASE_MOVE_CONFIRM = {
  isOpen: false,
  cardId: null,
  petName: '',
  toPhase: '',
  fromDrag: false,
  moveParams: null,
};

const getAppointmentMoveDetails = ({
  cardDetails,
  initialPhaseId,
  targetPhaseId,
}) => ({
  petName: cardDetails.metadata.petName,
  fromPhase: PHASES[+initialPhaseId],
  toPhase: PHASES[+targetPhaseId],
});

function getCardColor(laneId) {
  const { appointmentPhases } = constant;
  return Object.values(appointmentPhases).find(({ id }) => `${id}` === laneId)
    .color;
}

function getAlertMessage(metadata) {
  const { petParentStatus, petParentName, petName } = metadata;
  if (petParentStatus === constant.STATUS.INACTIVE) {
    return `${petParentName} is inactive. Please activate parent to update the procedure.`;
  } else {
    return `${petName} is inactive. Please activate pet to update the procedure.`;
  }
}

function getVetPhasePickerOptions(
  currentPhaseId,
  direction,
  hiddenPhaseIds = [],
  phaseRemember = false,
) {
  const currentId = +currentPhaseId;
  const hiddenSet = phaseRemember
    ? new Set((hiddenPhaseIds || []).map((id) => +id))
    : null;

  return defaultLanes
    .filter((lane) => {
      const id = +lane.id;
      const inDirection =
        direction === 'previous' ? id < currentId : id > currentId;

      if (!inDirection) {
        return false;
      }

      if (hiddenSet?.has(id)) {
        return false;
      }

      return true;
    })
    .map((lane) => ({
      id: +lane.id,
      name: lane.title,
      color: getCardColor(lane.id),
    }));
}

const BOARDING_PHASE_ID = constant.appointmentPhases.boarding.id;

const BOARDING_FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'notSent', label: 'Not sent' },
  { key: 'sentToday', label: 'Sent today' },
];

const filterBoardingAppointments = (events, boardingFilter) => {
  if (!events || boardingFilter === 'all') return events || [];
  const today = new Date().toDateString();
  return events.filter((appt) => {
    if (`${appt.phase}` !== `${BOARDING_PHASE_ID}`) return true;
    const sentToday = (appt.ptbSentMessages || []).some(
      (msg) =>
        !msg.isDragMessage &&
        new Date(msg.dateTime).toDateString() === today,
    );
    return boardingFilter === 'sentToday' ? sentToday : !sentToday;
  });
};

const EXPECTED_PHASE_ID = constant.appointmentPhases.expected.id;

function scrollBoardToLaneIndex(boardEl, laneIndex, topScrollEl) {
  if (!boardEl || laneIndex < 0) return;

  const laneEls = boardEl.querySelectorAll('.react-trello-lane');
  const laneEl = laneEls[laneIndex];
  if (!laneEl) return;

  const boardRect = boardEl.getBoundingClientRect();
  const laneRect = laneEl.getBoundingClientRect();
  const delta = laneRect.left - boardRect.left;

  if (Math.abs(delta) < 2) return;

  const nextScrollLeft = boardEl.scrollLeft + delta;
  boardEl.scrollLeft = nextScrollLeft;
  if (topScrollEl) {
    topScrollEl.scrollLeft = nextScrollLeft;
  }
}

function pinCardFirst(cards, appointmentId) {
  if (!appointmentId || !cards?.length) return cards;

  const id = String(appointmentId);
  const index = cards.findIndex((card) => String(card.id) === id);
  if (index <= 0) return cards;

  const next = [...cards];
  const [card] = next.splice(index, 1);
  next.unshift(card);
  return next;
}

function applyPinnedFirstToLanes(lanes, appointmentId) {
  if (!appointmentId) return lanes;

  return lanes.map((lane) => ({
    ...lane,
    cards: pinCardFirst(lane.cards, appointmentId),
  }));
}

const prepareLanes = (events, lanes) =>
  lanes.map((lane) => ({
    ...lane,
    cards: events
      .filter((event) => lane.id === `${event.phase}`)
      .map((event) => ({
        id: event._id,
        firstName: event.petName,
        lastName: event.parentLastName,
        parentName: event.parentName,
        petImage: event.petImage,
        doctorName: event.doctorName || '',
        petDetails: [event.petSpecies, event.petBreed]
          .filter(Boolean)
          .join(', '),
        appointmentType: event.type || event.typeTitle || '',
        phaseName: PHASES[+lane.id] || '',
        date: event.date,
        time: event.time,
        createdFromTrackingBoard: !!event.createdFromTrackingBoard,
        status: event.status,
        color: getCardColor(lane.id),
        laneId: `${lane.id}`,
        metadata: {
          petId: event.petId,
          type: event.type,
          petName: event.petName,
          petParentStatus: event.parentFacilityStatus || 'N/A',
          petParentName: event.parentName,
          facilityId: event.facility,
        },
      })),
  }));

function BoardingPhaseHeader({
  title,
  phaseColor,
  boardingFilter,
  onBoardingFilterChange,
  wide = false,
  boardViewMode = 'board',
  onToggleBoardViewMode,
}) {
  const gridViewActive = boardViewMode === 'grid';

  return (
    <div
      className={`ptb-lane-header${
        wide ? ' ptb-boarding-grid__header' : ''
      }`}
      style={{ borderTop: `7px solid ${phaseColor}` }}
    >
      <span className="ptb-lane-title">{title}</span>
      <div className="ptb-boarding-filters">
        {BOARDING_FILTER_OPTIONS.map((opt, index) => (
          <React.Fragment key={opt.key}>
            {index > 0 && (
              <span className="ptb-boarding-filter-divider">|</span>
            )}
            <button
              type="button"
              className={`ptb-boarding-filter${
                boardingFilter === opt.key ? ' is-active' : ''
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onBoardingFilterChange(opt.key);
              }}
            >
              {opt.label}
            </button>
          </React.Fragment>
        ))}
      </div>
      <button
        type="button"
        className="ptb-view-toggle"
        aria-pressed={gridViewActive}
  aria-label={
    gridViewActive ? 'Switch to list view' : 'Switch to grid view'
  }
  title={
    gridViewActive ? 'Switch to list view' : 'Switch to grid view'
  }
  onClick={(e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleBoardViewMode();
  }}
>
  {gridViewActive ? 'List' : 'Grid'}
      </button>
    </div>
  );
}
const Board = ({
  toggleClass,
  minimize,
  events,
  openPetModel,
  dragCard,
  deletePhaseMessage,
  search = '',
  hiddenPhaseIds = [],
  phaseRemember = false,
  boardViewMode = 'board',
  onToggleBoardViewMode,
  onReloadBoard,
  cardAck = null,
  onCardAckDismiss,
  newlyAddedAppointmentId = null,
  onNewlyAddedFeedbackComplete,
}) => {
  const boardScroollRef = useRef();
  const scrollRef = useRef();
  const boardRef = useRef();
  const phasePickerRef = useRef();
  const prevSearchRef = useRef(search);
  const vetAddScrollDoneRef = useRef(false);
  const [pinnedHighlightId, setPinnedHighlightId] = useState(null);
  const facility = useSelector((state) => state?.user?.profile?.facility);
  const { data: services } = useGetFacilityServices();

  const baseLanes = useMemo(
    () => (services?.isBoardingOnly ? boardingOnlyLanes : defaultLanes),
    [services?.isBoardingOnly],
  );

  const boardingLaneDef = useMemo(
    () =>
      baseLanes.find((lane) => `${lane.id}` === `${BOARDING_PHASE_ID}`) ||
      null,
    [baseLanes],
  );

  const visibleLanes = useMemo(
    () => getVisibleLanes(baseLanes, hiddenPhaseIds),
    [baseLanes, hiddenPhaseIds],
  );

  const [eventBus, setEventBus] = useState(null);
  const [dragMove, setDragMove] = useState({
    cardId: '',
    sourceLaneId: '',
    targetLaneId: '',
    card: null,
  });
  const [ptbClass, setPtbClass] = useState('');
  const [boardingFilter, setBoardingFilter] = useState('all');
  const [phasePicker, setPhasePicker] = useState(EMPTY_PHASE_PICKER);

  const [eventData, setEventsData] = useState({
    lanes: visibleLanes.map((lane) => ({ ...lane })),
    boardingLaneCards: [],
  });
  const [confirmPopup, setConfirmPopup] = useState({
    showResponse: false,
    responseMessage: '',
    responseType: '',
    responseAlertType: '',
    footer: false,
  });

  const [phaseMoveConfirm, setPhaseMoveConfirm] = useState(
    EMPTY_PHASE_MOVE_CONFIRM,
  );
  const [isPhaseMovePending, setIsPhaseMovePending] = useState(false);
  const [phaseMoveAck, setPhaseMoveAck] = useState(null);
  const [pendingPhaseMoveAck, setPendingPhaseMoveAck] = useState(null);

  const clearPhaseMoveAck = useCallback(() => {
    setPhaseMoveAck(null);
  }, []);

  const queuePhaseMoveAck = useCallback(
    ({ cardId, petName, phaseName, targetPhaseId, fromDrag = false }) => {
      if (services?.isBoardingOnly) {
        setPhaseMoveAck({ cardId, petName, phaseName });
        return;
      }

      setPendingPhaseMoveAck({
        cardId,
        petName,
        phaseName,
        targetPhaseId,
        scrollToDestination: !fromDrag,
      });
    },
    [services?.isBoardingOnly],
  );

const fillBoardWithAppointments = useCallback(
  (boardEvents) => {
    const nextEvents = services?.isBoardingOnly
      ? filterBoardingAppointments(boardEvents, boardingFilter)
      : boardEvents;
    let lanes = prepareLanes(nextEvents || [], visibleLanes);
    let boardingLaneCards = boardingLaneDef
      ? prepareLanes(nextEvents || [], [boardingLaneDef])[0]?.cards || []
      : [];

    if (pinnedHighlightId) {
      lanes = applyPinnedFirstToLanes(lanes, pinnedHighlightId);
      boardingLaneCards = pinCardFirst(
        boardingLaneCards,
        pinnedHighlightId,
      );
    }

    setEventsData({
      lanes,
      boardingLaneCards,
    });
  },
  [
    visibleLanes,
    boardingLaneDef,
    services?.isBoardingOnly,
    boardingFilter,
    pinnedHighlightId,
  ],
);

  const onRefreshClick = useCallback(() => {
    onReloadBoard?.();
  }, [onReloadBoard]);

  useEffect(() => {
    if (events) {
      fillBoardWithAppointments(events);
    }
}, [toggleClass, events, fillBoardWithAppointments]);

useEffect(() => {
  if (!newlyAddedAppointmentId) {
    vetAddScrollDoneRef.current = false;
    return undefined;
  }

  const id = String(newlyAddedAppointmentId);
  setPinnedHighlightId(id);

  const timer = setTimeout(() => {
    setPinnedHighlightId(null);
    onNewlyAddedFeedbackComplete?.();
  }, PTB_NEWLY_ADDED_FEEDBACK_MS);

  return () => clearTimeout(timer);
}, [newlyAddedAppointmentId, onNewlyAddedFeedbackComplete]);

useEffect(() => {
  if (!services?.isBoardingOnly || !newlyAddedAppointmentId) return;
  setBoardingFilter((prev) => (prev === 'sentToday' ? 'all' : prev));
}, [newlyAddedAppointmentId, services?.isBoardingOnly]);

useEffect(() => {
  if (services?.isBoardingOnly || !pinnedHighlightId) return;
  if (vetAddScrollDoneRef.current) return;

  const laneIndex = eventData.lanes.findIndex(
    (lane) => `${lane.id}` === `${EXPECTED_PHASE_ID}`,
  );
  if (laneIndex < 0) return;

  const expectedLane = eventData.lanes[laneIndex];
  if (
    !expectedLane?.cards?.some(
      (card) => String(card.id) === String(pinnedHighlightId),
    )
  ) {
    return;
  }

  vetAddScrollDoneRef.current = true;

  const frameId = requestAnimationFrame(() => {
    scrollBoardToLaneIndex(
      boardScroollRef.current,
      laneIndex,
      scrollRef.current,
    );
  });

  return () => cancelAnimationFrame(frameId);
}, [eventData, pinnedHighlightId, services?.isBoardingOnly]);

useEffect(() => {
  if (services?.isBoardingOnly) return;

  const trimmedSearch = (search || '').trim();
    const prevTrimmed = (prevSearchRef.current || '').trim();
    prevSearchRef.current = search;

    if (!trimmedSearch) {
      if (!prevTrimmed) return;

      const frameId = requestAnimationFrame(() => {
        const boardEl = boardScroollRef.current;
        if (!boardEl) return;
        boardEl.scrollLeft = 0;
        if (scrollRef.current) {
          scrollRef.current.scrollLeft = 0;
        }
      });
      return () => cancelAnimationFrame(frameId);
    }

    const lanes = eventData?.lanes || [];
    const targetLaneIndex = lanes.findIndex(
      (lane) => lane.cards && lane.cards.length > 0,
    );
    if (targetLaneIndex < 0) return;

    const frameId = requestAnimationFrame(() => {
      scrollBoardToLaneIndex(
        boardScroollRef.current,
        targetLaneIndex,
        scrollRef.current,
      );
    });

    return () => cancelAnimationFrame(frameId);
  }, [eventData, search, services?.isBoardingOnly]);

useEffect(() => {
  if (services?.isBoardingOnly || !pendingPhaseMoveAck) {
    return undefined;
  }

  const {
    cardId,
    petName,
    phaseName,
    targetPhaseId,
    scrollToDestination,
  } = pendingPhaseMoveAck;

  const lanes = eventData?.lanes || [];
  const isCardInDestinationLane = lanes.some(
    (lane) =>
      `${lane.id}` === `${targetPhaseId}` &&
      lane.cards?.some((card) => String(card.id) === String(cardId)),
  );

  if (!isCardInDestinationLane) {
    return undefined;
  }

  const revealAck = () => {
    setPhaseMoveAck({ cardId, petName, phaseName });
    setPendingPhaseMoveAck(null);
  };

  if (!scrollToDestination) {
    revealAck();
    return undefined;
  }

  const laneIndex = lanes.findIndex(
    (lane) => `${lane.id}` === `${targetPhaseId}`,
  );
  if (laneIndex < 0) {
    revealAck();
    return undefined;
  }

  const frameId = requestAnimationFrame(() => {
    scrollBoardToLaneIndex(
      boardScroollRef.current,
      laneIndex,
      scrollRef.current,
    );
    requestAnimationFrame(revealAck);
  });

  return () => cancelAnimationFrame(frameId);
}, [eventData, pendingPhaseMoveAck, services?.isBoardingOnly]);

  useEffect(() => {
    fetchToken();
    const unsubscribe = onMessageListener(() => {
      onRefreshClick();
    });

    return async () => {
      unsubscribe?.();
      const token = localStorage.getFromStorage('firebase_token');
      if (token) {
        await unsubscribeFromFirebaseTopic({ token });
      }
    };
  }, [facility?._id, onRefreshClick]);

  useEffect(() => {
    if (!phasePicker.isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setPhasePicker(EMPTY_PHASE_PICKER);
      }
    };

    const handleMouseDown = (event) => {
      if (
        phasePickerRef.current &&
        !phasePickerRef.current.contains(event.target)
      ) {
        setPhasePicker(EMPTY_PHASE_PICKER);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [phasePicker.isOpen]);

  const showAlert = (
    message,
    { type = 'ok', alertType = 'danger', onSuccess, onCancel, subMessage } = {},
  ) => {
    setConfirmPopup({
      showResponse: true,
      subMessage,
      onSuccess,
      onCancel,
      responseMessage: message,
      responseType: type,
      responseAlertType: alertType,
    });
  };

  const closeAlert = useCallback(() => {
    setConfirmPopup({
      showResponse: false,
      responseMessage: '',
      onSuccess: undefined,
      onCancel: undefined,
      responseType: '',
      responseAlertType: '',
      footer: false,
    });
  }, []);

  const moveCardBack = useCallback(
    ({ from, to, card, cardId }) => {
      if (!from || !to) return;

      eventBus?.publish({
        type: 'MOVE_CARD',
        fromLaneId: from,
        toLaneId: to,
        cardId,
        index: 0,
      });
      eventBus?.publish({
        type: 'UPDATE_CARD',
        laneId: to,
        card: {
          ...card,
          laneId: to,
          color: getCardColor(to),
        },
      });
    },
    [eventBus],
  );

  const moveCardToPhase = async ({
    dragCardId,
    sourceId,
    targetId,
    dragCardDetails,
    fromDrag = false,
  }) => {
    setPtbClass('');

    if (dragCardDetails.metadata.petParentStatus === constant.STATUS.INACTIVE) {
      showAlert(getAlertMessage(dragCardDetails.metadata), {
        type: 'ok',
        alertType: 'danger',
      });
      return false;
    }

    const startOfTheDay = moment().startOf('date').valueOf();
    const appointment = events
      .filter((event) => event._id === dragCardId)
      .shift();
    const appointmentTime = moment(appointment?.dateTime).valueOf();

    if (targetId === 1 && startOfTheDay > appointmentTime) {
      showAlert(ResponseMessages.WARN.CAN_NOT_MOVE_TO_EXPECTED, {
        type: 'alert',
        alertType: 'info',
      });
      return false;
    }

    if (targetId < sourceId) {
      await onCardMoveBack({
        cardId: dragCardId,
        cardDetails: dragCardDetails,
        targetLaneId: targetId,
        fromDrag,
      });
    } else if (targetId !== sourceId) {
      const error = await dragCard({
        appointmentId: dragCardId,
        phase: targetId,
      });
      if (error) {
        showAlert(error.errorMessage);
      } else {
        const { petName, toPhase } = getAppointmentMoveDetails({
          cardDetails: dragCardDetails,
          initialPhaseId: sourceId,
          targetPhaseId: targetId,
        });
        queuePhaseMoveAck({
          cardId: dragCardId,
          petName,
          phaseName: toPhase,
          targetPhaseId: targetId,
          fromDrag,
        });
      }
    }

    return true;
  };

  const onCardMoveBack = async ({
    cardId,
    cardDetails,
    targetLaneId,
    fromDrag = false,
  }) => {
    closeAlert();

    await deletePhaseMessage({
      phaseId: targetLaneId,
      appointmentId: cardId,
    });
    const error = await dragCard({
      appointmentId: cardId,
      phase: targetLaneId,
    });
    if (error) {
      return showAlert(error.errorMessage);
    }
    const { petName, toPhase } = getAppointmentMoveDetails({
      cardDetails,
      initialPhaseId: cardDetails?.laneId,
      targetPhaseId: targetLaneId,
    });
    queuePhaseMoveAck({
      cardId,
      petName,
      phaseName: toPhase,
      targetPhaseId: targetLaneId,
      fromDrag,
    });
  };

  const handlePhaseMoveConfirm = async () => {
    if (isPhaseMovePending || !phaseMoveConfirm.moveParams) {
      return;
    }

    const moveParams = phaseMoveConfirm.moveParams;

    setIsPhaseMovePending(true);
    setPhaseMoveConfirm(EMPTY_PHASE_MOVE_CONFIRM);

    try {
      await moveCardToPhase(moveParams);
    } finally {
      setIsPhaseMovePending(false);
    }
  };

  const handlePhaseMoveCancel = useCallback(() => {
    setPhaseMoveConfirm((current) => {
      if (current.fromDrag && current.moveParams) {
        const {
          dragCardId,
          sourceId,
          targetId,
          dragCardDetails,
        } = current.moveParams;

        moveCardBack({
          cardId: dragCardId,
          from: targetId,
          to: sourceId,
          card: dragCardDetails,
        });
      }

      return EMPTY_PHASE_MOVE_CONFIRM;
    });
    setIsPhaseMovePending(false);
  }, [moveCardBack]);

  const requestPhaseMove = ({
    dragCardId,
    sourceId,
    targetId,
    position = 0,
    dragCardDetails,
    fromDrag = false,
  }) => {
    if (`${targetId}` === `${sourceId}`) {
      return;
    }

    if (fromDrag) {
      setDragMove({
        card: dragCardDetails,
        cardId: dragCardId,
        sourceLaneId: sourceId,
        targetLaneId: targetId,
      });
    }

    if (+targetId === 1) {
      return showAlert(
        `It's not possible to move appointment back to Expected phase`,
        {
          type: 'ok',
          alertType: 'danger',
          onCancel: () => {
            if (fromDrag) {
              moveCardBack({
                cardId: dragCardId,
                from: targetId,
                to: sourceId,
                card: dragCardDetails,
              });
            }
            closeAlert();
          },
        },
      );
    }

    const { petName, toPhase } = getAppointmentMoveDetails({
      cardDetails: dragCardDetails,
      initialPhaseId: sourceId,
      targetPhaseId: targetId,
    });

    setPhaseMoveConfirm({
      isOpen: true,
      cardId: dragCardId,
      petName,
      toPhase,
      fromDrag,
      moveParams: {
        dragCardId,
        sourceId,
        targetId,
        position,
        dragCardDetails,
        fromDrag,
      },
    });
  };

  const handleDragEnd = (
    dragCardId,
    sourceId,
    targetId,
    position,
    dragCardDetails,
  ) => {
    if (targetId === sourceId) return;

    requestPhaseMove({
      dragCardId,
      sourceId,
      targetId,
      position,
      dragCardDetails,
      fromDrag: true,
    });
  };

  const handleOpenPhasePicker = ({
    direction,
    anchorRect,
    cardId,
    sourceId,
    cardDetails,
  }) => {
    setPhasePicker({
      isOpen: true,
      direction,
      anchorRect,
      cardId,
      sourceId,
      cardDetails,
    });
  };

  const handlePhasePickerSelect = (targetPhaseId) => {
    const { cardId, sourceId, cardDetails } = phasePicker;
    setPhasePicker(EMPTY_PHASE_PICKER);

    requestPhaseMove({
      dragCardId: cardId,
      sourceId,
      targetId: `${targetPhaseId}`,
      dragCardDetails: cardDetails,
      fromDrag: false,
    });
  };

  const onCardClick = (cardId, metadata, laneId) => {
    if (metadata.petParentStatus === constant.STATUS.INACTIVE) {
      showAlert(getAlertMessage(metadata));
    }
    requestAnimationFrame(() => {
      openPetModel(cardId, metadata, laneId);
    });
  };

  const onConfirmClose = useCallback(() => {
    closeAlert();
  }, [closeAlert]);

  const handleDragStart = () => {
    setPtbClass('ptb-board');
  };

  const handleScroll = (event) => {
    const targetDiv = event.target;

    if (targetDiv === boardScroollRef.current && scrollRef.current) {
      scrollRef.current.scrollLeft = targetDiv.scrollLeft;
    } else if (targetDiv === scrollRef.current && boardScroollRef.current) {
      boardScroollRef.current.scrollLeft = targetDiv.scrollLeft;
    }
  };

  const onBoardDataChange = (newData) => {
    const boardingLane = newData.lanes?.find(
      (lane) => `${lane.id}` === `${BOARDING_PHASE_ID}`,
    );

    setEventsData((prev) => ({
      lanes: newData.lanes,
      boardingLaneCards:
        boardingLane?.cards ?? prev.boardingLaneCards ?? [],
    }));

    if (dragMove?.card) {
      eventBus?.publish({
        type: 'UPDATE_CARD',
        laneId: dragMove.targetLaneId,
        card: {
          ...dragMove.card,
          color: getCardColor(dragMove.targetLaneId),
        },
      });
    }
  };

  const boardWidth = boardRef?.current?.scrollWidth;

  const phasePickerOptions = phasePicker.isOpen
    ? getVetPhasePickerOptions(
        phasePicker.sourceId,
        phasePicker.direction,
        hiddenPhaseIds,
        phaseRemember,
      )
    : [];

  const phasePickerStyle = phasePicker.anchorRect
    ? {
        top: phasePicker.anchorRect.bottom + 4,
        left: phasePicker.anchorRect.left,
      }
    : undefined;

  const renderLaneHeader = useCallback(
    ({ id, title }) => {
      const showBoardingFilters =
        services?.isBoardingOnly && `${id}` === `${BOARDING_PHASE_ID}`;
      const phaseColor = getCardColor(`${id}`);

      if (showBoardingFilters) {
        return (
          <BoardingPhaseHeader
            title={title}
            phaseColor={phaseColor}
            boardingFilter={boardingFilter}
            onBoardingFilterChange={setBoardingFilter}
            boardViewMode={boardViewMode}
            onToggleBoardViewMode={onToggleBoardViewMode}
          />
        );
      }

      return (
        <div
          className="ptb-lane-header"
          style={{ borderTop: `7px solid ${phaseColor}` }}
        >
          <span className="ptb-lane-title">{title}</span>
        </div>
      );
    },
    [
      services?.isBoardingOnly,
      boardingFilter,
      boardViewMode,
      onToggleBoardViewMode,
    ],
  );

  const showGridView =
    services?.isBoardingOnly && boardViewMode === 'grid';

  const gridBoardingCards = useMemo(() => {
    const visibleBoardingLane = eventData.lanes?.find(
      (lane) => `${lane.id}` === `${BOARDING_PHASE_ID}`,
    );
    if (visibleBoardingLane?.cards) {
      return visibleBoardingLane.cards;
    }
    return eventData.boardingLaneCards || [];
  }, [eventData]);

  const boardingPhaseTitle =
    boardingLaneDef?.title || PHASES[BOARDING_PHASE_ID] || 'Boarding';

  return (
    <div className={`board ${toggleClass}`}>
      {toggleClass === 'maximize' && (
        <div className="refresh-btn-board-container">
          <div className="icon-minimize" style={{ width: '300px' }}>
            <span onClick={minimize} className="lnr lnr-frame-contract" />
          </div>
          <RefreshBoardButton onRefreshClick={onRefreshClick} />
        </div>
      )}

      {!showGridView ? (
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-top-scroll-wrapper sticky-top-60"
          style={{
            visibility:
              boardWidth <= boardScroollRef?.current?.clientWidth
                ? 'hidden'
                : 'inherit',
          }}
        >
          <div style={boardWidth ? { width: boardWidth } : {}} />
        </div>
      ) : null}

      <div
        ref={boardScroollRef}
        onScroll={showGridView ? undefined : handleScroll}
        className={`board-scroll-wrapper${
          showGridView ? ' ptb-boarding-grid-scroll' : ''
        }`}
      >
        {showGridView ? (
          <div className="ptb-boarding-grid">
            <BoardingPhaseHeader
              title={boardingPhaseTitle}
              phaseColor={getCardColor(`${BOARDING_PHASE_ID}`)}
              boardingFilter={boardingFilter}
              onBoardingFilterChange={setBoardingFilter}
              boardViewMode={boardViewMode}
              onToggleBoardViewMode={onToggleBoardViewMode}
              wide
            />
            <div className="ptb-boarding-grid__cards">
              {gridBoardingCards.map((card) => (
                <div key={card.id} className="ptb-boarding-grid__cell">
                  <PetCard
                    {...card}
                    mode="boarding"
                    highlightNewlyAdded={
                      String(card.id) === String(pinnedHighlightId)
                    }
                    onCardClick={onCardClick}
                    onOpenPhasePicker={undefined}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            ref={boardRef}
            className={`board-fit-content${
              services?.isBoardingOnly ? ' boarding-only-board' : ''
            }`}
          >
            <TrelloBoard
              eventBusHandle={setEventBus}
              laneDraggable={false}
              onDataChange={onBoardDataChange}
              handleDragStart={handleDragStart}
              handleDragEnd={handleDragEnd}
              dragClass="dragClass"
              className={`react-trello-board ${ptbClass}`}
              data={{ lanes: eventData.lanes }}
              hideCardDeleteIcon
              components={{
                LaneHeader: renderLaneHeader,
                Section: PtbLaneSection,
                Card: (props) => (
                  <PetCard
                    {...props}
                    mode={
                      services?.isBoardingOnly ? 'boarding' : 'veterinary'
                    }
                    highlightNewlyAdded={
                      String(props.id) === String(pinnedHighlightId)
                    }
                    onCardClick={onCardClick}
                    onOpenPhasePicker={
                      services?.isBoardingOnly
                        ? undefined
                        : handleOpenPhasePicker
                    }
                  />
                ),
              }}
            />
          </div>
        )}
      </div>

      {phasePicker.isOpen ? (
        <div
          ref={phasePickerRef}
          className="ptb-phase-picker"
          style={phasePickerStyle}
        >
          <div className="ptb-phase-picker__options">
            {phasePickerOptions.map((phase) => (
              <button
                key={phase.id}
                type="button"
                className="ptb-phase-picker__option"
                style={{ backgroundColor: phase.color }}
                onClick={() => handlePhasePickerSelect(phase.id)}
              >
                {phase.name}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <PhaseMoveConfirmPopover
        isOpen={phaseMoveConfirm.isOpen}
        cardId={phaseMoveConfirm.cardId}
        petName={phaseMoveConfirm.petName}
        toPhase={phaseMoveConfirm.toPhase}
        onConfirm={handlePhaseMoveConfirm}
        onCancel={handlePhaseMoveCancel}
        isMoving={isPhaseMovePending}
        scrollContainerRef={boardScroollRef}
        preferredPlacement={
          phaseMoveConfirm.fromDrag ? 'above' : 'below'
        }
      />

      {phaseMoveAck ? (
        <PtbCardAnchoredAck
          cardId={phaseMoveAck.cardId}
          petName={phaseMoveAck.petName}
          phaseName={phaseMoveAck.phaseName}
          scrollContainerRef={boardScroollRef}
          onDismiss={clearPhaseMoveAck}
        />
      ) : cardAck ? (
        <PtbCardAnchoredAck
          cardId={cardAck.cardId}
          message={cardAck.message}
          scrollContainerRef={boardScroollRef}
          onDismiss={onCardAckDismiss}
        />
      ) : null}

      <MessageModal
        show={
          confirmPopup.showResponse &&
          confirmPopup.responseType !== 'confirm'
        }
        type={confirmPopup.responseType}
        alertType={confirmPopup.responseAlertType}
        message={confirmPopup.responseMessage}
        subMessage={confirmPopup.subMessage}
        onSuccess={confirmPopup.onSuccess || onCardMoveBack}
        onClose={confirmPopup.onCancel || onConfirmClose}
        footer
      />
    </div>
  );
};

const mapDispatchToProps = {
  dragCard,
  deletePhaseMessage,
};

export default connect(null, mapDispatchToProps)(Board);
